"use client";

import Link from "next/link";
import { useEffect } from "react";

import Text from "@/components/primitives/Text/Text";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/core/i18n";
import { logger } from "@/core/logging";
import { addMonitoringError } from "@/core/monitoring";

import "@/styles/index.css";

type ErrorPageProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

const DEV_ENVIRONMENT_NAMES = new Set(["development", "preview"]);

export default function AssessmentError({ error, reset }: ErrorPageProps) {
  const { t } = useTranslation();
  const shouldShowErrorDetails =
    process.env.NODE_ENV === "development" ||
    DEV_ENVIRONMENT_NAMES.has(process.env.NEXT_PUBLIC_ENVIRONMENT_NAME ?? "");

  useEffect(() => {
    logger.error("assessment_error_boundary", { digest: error.digest ?? "" }, error);
    addMonitoringError(error, {
      name: "assessment_error_boundary",
      digest: error.digest ?? "",
    });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
      <div className="flex flex-col items-center gap-2">
        <Text component="h1" text={t("error.title")} variant="heading-xl" />
        <div className="max-w-md">
          <Text component="p" text={t("error.description")} tone="muted" variant="body-md" />
        </div>
        {shouldShowErrorDetails && (
          <div className="border-border bg-muted/30 mt-4 w-full max-w-md rounded-md border p-4 text-left">
            <Text component="h2" text="Error details (dev only)" variant="body-sm" weight="bold" />
            <div className="mt-2 space-y-1">
              <Text component="p" text={`Name: ${error.name}`} variant="body-sm" />
              <Text component="p" text={`Message: ${error.message}`} variant="body-sm" />
              {error.digest && (
                <Text component="p" text={`Digest: ${error.digest}`} variant="body-sm" />
              )}
            </div>
            {error.stack && (
              <pre className="mt-2 max-h-48 overflow-auto text-xs break-all whitespace-pre-wrap">
                {error.stack}
              </pre>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" onClick={reset}>
          <Text component="span" text={t("error.tryAgain")} />
        </Button>
        <Button asChild variant="outline">
          <Link href="/">
            <Text component="span" text={t("error.goHome")} />
          </Link>
        </Button>
      </div>
    </div>
  );
}
